/* tslint:disable */
/* eslint-disable */
/**
 * CourierApi
 * CourierApi
 *
 * The version of the OpenAPI document: 2.1.0
 *
 *
 * NOTE: Manually maintained — do NOT regenerate. See CLAUDE.md "Important Notes" #1.
 */

import * as runtime from '../runtime';
import type {
  NotificationTemplate,
  NotificationTemplateBuiltInPreview,
  NotificationTemplateSendTestRequest,
  NotificationTemplateSendTestResult,
} from '../models/NotificationTemplate';
import {
  NotificationTemplateFromJSON,
  NotificationTemplateToJSON,
} from '../models/NotificationTemplate';

export interface NotificationTemplateFindRequest {
  filter?: string;
}

export interface NotificationTemplateFindByIdRequest {
  id: string;
  filter?: string;
}

export interface NotificationTemplateCreateRequest {
  data: Partial<NotificationTemplate>;
}

export interface NotificationTemplatePatchRequest {
  id: string;
  data: Partial<NotificationTemplate>;
}

export interface NotificationTemplateDeleteRequest {
  id: string;
}

export interface NotificationTemplateBuiltInPreviewRequest {
  event: string;
  channel: string;
  /** Preview as this courier instead of the caller (Overlord only). */
  realm?: string;
}

export interface NotificationTemplateSendTestOperationRequest {
  data: NotificationTemplateSendTestRequest;
}

/**
 * NotificationTemplateApi — per-courier notification message overrides.
 */
export class NotificationTemplateApi extends runtime.BaseAPI {
  /**
   * List notification templates (auto-filtered to the caller's realm).
   */
  async notificationTemplateFind(
    requestParameters: NotificationTemplateFindRequest = {},
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<NotificationTemplate[]> {
    const queryParameters: any = {};
    if (requestParameters.filter !== undefined) {
      queryParameters['filter'] = requestParameters.filter;
    }

    const response = await this.request(
      {
        path: `/NotificationTemplates`,
        method: 'GET',
        headers: {},
        query: queryParameters,
      },
      initOverrides
    );

    const data = await response.json();
    const arr = (data && (data as any).result) || data;
    return Array.isArray(arr) ? arr.map(NotificationTemplateFromJSON) : [];
  }

  /**
   * Fetch a single template by id.
   */
  async notificationTemplateFindById(
    requestParameters: NotificationTemplateFindByIdRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<NotificationTemplate> {
    if (requestParameters.id == null) {
      throw new runtime.RequiredError(
        'id',
        'Required parameter "id" was null or undefined when calling notificationTemplateFindById.'
      );
    }

    const queryParameters: any = {};
    if (requestParameters.filter !== undefined) {
      queryParameters['filter'] = requestParameters.filter;
    }

    const response = await this.request(
      {
        path: `/NotificationTemplates/${encodeURIComponent(String(requestParameters.id))}`,
        method: 'GET',
        headers: {},
        query: queryParameters,
      },
      initOverrides
    );

    const data = await response.json();
    return NotificationTemplateFromJSON(data);
  }

  /**
   * Create a new template.
   */
  async notificationTemplateCreate(
    requestParameters: NotificationTemplateCreateRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<NotificationTemplate> {
    if (requestParameters.data == null) {
      throw new runtime.RequiredError(
        'data',
        'Required parameter "data" was null or undefined when calling notificationTemplateCreate.'
      );
    }

    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    const response = await this.request(
      {
        path: `/NotificationTemplates`,
        method: 'POST',
        headers: headerParameters,
        query: {},
        body: NotificationTemplateToJSON(
          requestParameters.data as NotificationTemplate
        ),
      },
      initOverrides
    );

    const data = await response.json();
    return NotificationTemplateFromJSON(data);
  }

  /**
   * Update an existing template (partial fields).
   */
  async notificationTemplatePatch(
    requestParameters: NotificationTemplatePatchRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<NotificationTemplate> {
    if (requestParameters.id == null) {
      throw new runtime.RequiredError(
        'id',
        'Required parameter "id" was null or undefined when calling notificationTemplatePatch.'
      );
    }

    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    const response = await this.request(
      {
        path: `/NotificationTemplates/${encodeURIComponent(String(requestParameters.id))}`,
        method: 'PATCH',
        headers: headerParameters,
        query: {},
        body: NotificationTemplateToJSON(
          requestParameters.data as NotificationTemplate
        ),
      },
      initOverrides
    );

    const data = await response.json();
    return NotificationTemplateFromJSON(data);
  }

  /**
   * Delete a template — used for "Reset to Default" in the admin UI.
   */
  async notificationTemplateDelete(
    requestParameters: NotificationTemplateDeleteRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<void> {
    if (requestParameters.id == null) {
      throw new runtime.RequiredError(
        'id',
        'Required parameter "id" was null or undefined when calling notificationTemplateDelete.'
      );
    }

    await this.request(
      {
        path: `/NotificationTemplates/${encodeURIComponent(String(requestParameters.id))}`,
        method: 'DELETE',
        headers: {},
        query: {},
      },
      initOverrides
    );
  }

  /**
   * Fetch the built-in default subject/body for every supported
   * (event, channel) pair. The admin editor seeds new templates from
   * these so couriers customise from a working starting point.
   */
  async notificationTemplateDefaults(
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<
    Array<{
      event: string;
      channel: string;
      subject: string | null;
      body: string;
    }>
  > {
    const response = await this.request(
      {
        path: `/NotificationTemplates/defaults`,
        method: 'GET',
        headers: {},
        query: {},
      },
      initOverrides
    );

    const data = await response.json();
    const arr = (data && (data as any).result) || data;
    return Array.isArray(arr) ? arr : [];
  }

  /**
   * The built-in package status email a customer receives when the courier
   * keeps the default — the real delivered email, not the editor's starter
   * text from notificationTemplateDefaults.
   */
  async notificationTemplateBuiltInPreview(
    requestParameters: NotificationTemplateBuiltInPreviewRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<NotificationTemplateBuiltInPreview> {
    const query: runtime.HTTPQuery = {
      event: requestParameters.event,
      channel: requestParameters.channel,
    };
    if (requestParameters.realm) {
      query['realm'] = requestParameters.realm;
    }

    const response = await this.request(
      {
        path: `/NotificationTemplates/builtin-preview`,
        method: 'GET',
        headers: {},
        query,
      },
      initOverrides
    );

    return (await response.json()) as NotificationTemplateBuiltInPreview;
  }

  /**
   * Render a template and send it via the matching transport — lets the
   * admin verify output before going live. Pass `body` to preview unsaved edits.
   */
  async notificationTemplateSendTest(
    requestParameters: NotificationTemplateSendTestOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction
  ): Promise<NotificationTemplateSendTestResult> {
    if (requestParameters.data == null) {
      throw new runtime.RequiredError(
        'data',
        'Required parameter "data" was null or undefined when calling notificationTemplateSendTest.'
      );
    }

    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    const response = await this.request(
      {
        path: `/NotificationTemplates/sendTest`,
        method: 'POST',
        headers: headerParameters,
        query: {},
        body: requestParameters.data as any,
      },
      initOverrides
    );

    return (await response.json()) as NotificationTemplateSendTestResult;
  }
}
